import { PermissionStatus } from './types';

/**
 * The single signal a caller needs to decide what to render for a device kind. It
 * collapses the permission × presence permutation matrix into one discriminated value
 * so consumers never recombine raw permission strings with `enumerateDevices` presence
 * themselves (the fragmentation ADR 0001 set out to remove).
 *
 * Each value names a *distinct user-actionable situation*, not an internal cause:
 *  - `unknown`           — not resolved yet (permission or presence still loading). Render nothing / a spinner.
 *  - `available`         — a device is connected AND permission is granted. The ONLY state in which
 *                          Agora may create a track. `isDeviceAvailable` is exactly this.
 *  - `needs_permission`  — a device is connected and permission is still promptable. Show the "Allow" CTA.
 *  - `permission_denied` — the browser permission is blocked. Tell the user how to re-enable it in their browser.
 *  - `system_denied`     — the browser granted permission but the OS blocks the device (macOS/Windows privacy).
 *                          Point the user at OS settings, not the browser.
 *  - `no_device`         — permission is fine (granted or promptable) but no device of this kind is connected.
 *                          Tell the user to connect/insert one.
 *  - `unsupported`       — media capture is unavailable in this context (insecure context / old browser).
 */
export type DeviceAvailability =
  | 'unknown'
  | 'available'
  | 'needs_permission'
  | 'permission_denied'
  | 'system_denied'
  | 'no_device'
  | 'unsupported';

/**
 * Derive the {@link DeviceAvailability} for one kind from its ambient permission and raw
 * presence. Pure and total over every (permission, presence) pair so the module — not each
 * caller — owns the matrix.
 *
 * `present` is the raw per-kind `enumerateDevices` presence: `null` until first checked,
 * then `true`/`false`. A hard permission verdict (`denied`/`system_denied`/`unsupported`)
 * is reported regardless of presence, because that block is what the user must act on first;
 * only once permission could yield a device does presence decide `available` vs `no_device`.
 */
export const deriveDeviceAvailability = (permission: PermissionStatus, present: boolean | null): DeviceAvailability => {
  if (permission === 'unsupported') return 'unsupported';
  if (permission === 'denied') return 'permission_denied';
  if (permission === 'system_denied') return 'system_denied';
  if (permission === 'unknown') return 'unknown';

  // permission is 'granted' or 'prompt' — now presence decides.
  if (present === null) return 'unknown';
  if (!present) return 'no_device';

  return permission === 'granted' ? 'available' : 'needs_permission';
};

/** Convenience predicate: the kind is connected and granted, so a track may be created. */
export const isDeviceAvailable = (availability: DeviceAvailability): boolean => availability === 'available';
